import { FormikValues } from 'formik';
import { useMemo, useState, useEffect } from 'react';

import { isStaticFormField } from 'utils/static-form-field';
import {
  fixSubmissionTypes,
  splitFieldsByPages,
  getDataForFormikForm,
  collectOutcomesFields,
} from 'utils';
import {
  Form,
  FormField,
  FormFieldType,
  FormSubmissionData,
  FormSubmissionPayload,
  OutcomesFieldSubmissionValue,
} from 'types';

import { useAlert } from './alert';

const isInteractiveField = ({ type }: { type: FormFieldType }) =>
  type !== FormFieldType.PAGE_BREAK && !isStaticFormField(type);

interface UseFormBasicsProps {
  form?: Form;
  onFinish?: () => void;
  initialBlockIndex?: number;
  submissionData?: FormSubmissionData;
  lockSubmissionData?: FormSubmissionData;
  save: (values: FormSubmissionPayload) => { unwrap: () => Promise<void> };
}

interface FormStepPosition {
  pageIndex: number;
  blockIndex: number;
}

const comparePositions = (left: FormStepPosition, right: FormStepPosition) => {
  if (left.blockIndex !== right.blockIndex) {
    return left.blockIndex - right.blockIndex;
  }

  return left.pageIndex - right.pageIndex;
};

const hasOutcomeLockValue = (value: unknown) =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const collectOutcomeFieldIds = (fields: FormField[]): string[] =>
  collectOutcomesFields(fields).map((field) => field.id);

const getLockedPosition = (
  pagesByBlock: FormField[][][],
  submissionData?: FormSubmissionData
) => {
  if (!submissionData) {
    return undefined;
  }

  for (let blockIndex = 0; blockIndex < pagesByBlock.length; blockIndex += 1) {
    const pages = pagesByBlock[blockIndex];

    for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
      const outcomeFieldIds = collectOutcomeFieldIds(pages[pageIndex]);

      if (
        outcomeFieldIds.some((fieldId) =>
          hasOutcomeLockValue(
            submissionData[fieldId] as OutcomesFieldSubmissionValue
          )
        )
      ) {
        return { pageIndex, blockIndex };
      }
    }
  }

  return undefined;
};

export const useFormBasics = ({
  form,
  save,
  onFinish,
  submissionData,
  initialBlockIndex,
  lockSubmissionData,
}: UseFormBasicsProps) => {
  const { handleError } = useAlert();

  const { blocks, totalFieldsCount } = useMemo(() => {
    const blocks = form?.data.blocks ?? [];
    const count = blocks.reduce(
      (count, block) => count + block.fields.filter(isInteractiveField).length,
      0
    );
    return { blocks, totalFieldsCount: count };
  }, [form?.data.blocks]);

  const pagesByBlock = useMemo(
    () =>
      blocks.map((block) =>
        block.fields ? splitFieldsByPages(block.fields) : []
      ),
    [blocks]
  );

  const initialPosition = useMemo<FormStepPosition>(() => {
    const basePosition = {
      pageIndex: 0,
      blockIndex: initialBlockIndex ?? 0,
    };

    if (basePosition.blockIndex < 0) {
      return basePosition;
    }

    const initialLockedPosition = getLockedPosition(
      pagesByBlock,
      lockSubmissionData
    );

    if (
      initialLockedPosition &&
      comparePositions(initialLockedPosition, basePosition) > 0
    ) {
      return initialLockedPosition;
    }

    return basePosition;
  }, [initialBlockIndex, lockSubmissionData, pagesByBlock]);

  const [currentBlockIndex, setCurrentBlockIndex] = useState(
    initialPosition.blockIndex
  );
  const [page, setPage] = useState(initialPosition.pageIndex);
  const [latestSubmissionData, setLatestSubmissionData] =
    useState(submissionData);
  const [latestLockSubmissionData, setLatestLockSubmissionData] =
    useState(lockSubmissionData);

  const lockedPosition = useMemo(
    () => getLockedPosition(pagesByBlock, latestLockSubmissionData),
    [pagesByBlock, latestLockSubmissionData]
  );

  const { fieldsByPage, ...other } = useMemo(() => {
    const currentBlock = blocks[currentBlockIndex];
    const fieldsByPage = pagesByBlock[currentBlockIndex] ?? [];

    return {
      blocks,
      fieldsByPage,
      title: currentBlock?.name,
      previousBlocksFieldsLength:
        currentBlockIndex === 0
          ? 0
          : blocks
              ?.slice(0, currentBlockIndex)
              .map((b) => b.fields.filter(isInteractiveField).length)
              .reduce((prev, curr) => prev + curr, 0) || 0,
    };
  }, [blocks, currentBlockIndex, pagesByBlock]);

  const previousPagesFieldsLength = useMemo(() => {
    if (page === 0) {
      return 0;
    }
    return fieldsByPage
      .slice(0, page)
      .map((f) => f.filter(isInteractiveField).length)
      .reduce((prev, curr) => prev + curr, 0);
  }, [page, fieldsByPage]);

  const isLastBlock = currentBlockIndex === blocks.length - 1;
  const isLastPageOfStep =
    fieldsByPage.length > 1 ? page === fieldsByPage.length - 1 : true;
  const isLastPageAndBlock = isLastBlock && isLastPageOfStep;

  const currentFields = useMemo(
    () => fieldsByPage[page] ?? [],
    [fieldsByPage, page]
  );

  const formikProps = useMemo(() => {
    if (currentFields.length === 0) {
      return {
        initialValues: {} as FormikValues,
      };
    }
    return getDataForFormikForm(currentFields, latestSubmissionData ?? null);
  }, [currentFields, latestSubmissionData]);

  useEffect(() => {
    setLatestSubmissionData(submissionData);
  }, [submissionData]);

  useEffect(() => {
    setLatestLockSubmissionData(lockSubmissionData);
  }, [lockSubmissionData]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [currentFields]);

  useEffect(() => {
    if (currentBlockIndex < 0 || !lockedPosition) {
      return;
    }

    if (
      comparePositions(
        { pageIndex: page, blockIndex: currentBlockIndex },
        lockedPosition
      ) < 0
    ) {
      setCurrentBlockIndex(lockedPosition.blockIndex);
      setPage(lockedPosition.pageIndex);
    }
  }, [currentBlockIndex, page, lockedPosition]);

  const handleBack = () => {
    if (
      currentBlockIndex < 0 ||
      (lockedPosition &&
        comparePositions(
          { pageIndex: page, blockIndex: currentBlockIndex },
          lockedPosition
        ) <= 0)
    ) {
      return;
    }

    if (page === 0) {
      const newBlockIndex = currentBlockIndex - 1;
      const fieldsByPage = pagesByBlock[newBlockIndex] ?? [];
      setCurrentBlockIndex(newBlockIndex);
      setPage(Math.max(fieldsByPage.length - 1, 0));
    } else {
      setPage((prev) => prev - 1);
    }
  };

  const handleForward = () => {
    if (page === fieldsByPage.length - 1) {
      setCurrentBlockIndex((prev) => prev + 1);
      setPage(0);
    } else {
      setPage((prev) => prev + 1);
    }
  };

  const onSubmit = async (values: FormikValues) => {
    try {
      const allFields = blocks.flatMap((b) => b.fields);
      const payload = fixSubmissionTypes(
        {
          ...(latestSubmissionData || {}),
          ...values,
        },
        allFields
      );
      await save({
        data: payload,
        formId: form!.id,
        isSubmitted: isLastPageAndBlock,
      }).unwrap();
      setLatestSubmissionData(payload);
      setLatestLockSubmissionData(payload);

      if (isLastPageAndBlock) {
        onFinish?.();
      } else {
        handleForward();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const hasSteps = blocks.length > 1 || fieldsByPage.length > 1;
  const isFirstAvailableStep =
    currentBlockIndex >= 0 &&
    comparePositions(
      { pageIndex: page, blockIndex: currentBlockIndex },
      lockedPosition ?? { pageIndex: 0, blockIndex: 0 }
    ) <= 0;

  return {
    onSubmit,
    hasSteps,
    handleBack,
    formikProps,
    currentFields,
    totalFieldsCount,
    currentBlockIndex,
    isLastPageAndBlock,
    isFirstAvailableStep,
    setCurrentBlockIndex,
    previousPagesFieldsLength,
    blocksCount: blocks.length,
    ...other,
  };
};
